import { useEffect, useState } from 'react';
import axios from 'axios';

const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export function adbConnectionSignal(connection, now = Date.now()) {
  const name = connection?.authMode === 'tls' ? 'Wireless Debugging' : connection?.authMode === 'legacy' ? 'ADB Legacy' : 'ADB';
  const checked = connection?.checkedAtEpochMs;
  const due = connection?.nextCheckAtEpochMs;
  const expired = Number.isFinite(checked) && (checked > now + 2000
    || now - checked > 600000 || now > (Number.isFinite(due) && due > checked ? due : checked + 120000) + 45000);
  const status = expired && connection?.status === 'connected' ? 'stale' : connection?.status || 'checking';
  const labels = { unpaired: 'Unpaired', connected: 'Connected', disconnected: 'Disconnected',
    checking: 'Checking…', reconnecting: 'Reconnecting…', attention: 'Needs attention', unavailable: 'Status unavailable', stale: 'Verification stale' };
  return { label: `${name} ${labels[status]}`, className: status === 'connected' ? 'is-good'
    : ['disconnected', 'attention'].includes(status) ? 'is-bad' : 'is-muted' };
}

export function adbConnectionDetail(connection, now = Date.now()) {
  const checked = connection?.checkedAtEpochMs;
  if (!Number.isFinite(checked) || checked > now + 2000) return 'ADB check time unavailable';
  const seconds = Math.max(0, Math.floor((now - checked) / 1000));
  return `ADB checked ${seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m`} ago · separate from app connections`;
}

export default function useAreaConnections(siteId, devices) {
  const [lgTargets, setLgTargets] = useState({});
  const [connections, setConnections] = useState({ remotePairing: {}, adb: {}, nativeNodes: {} });
  // Stored device status refreshes frequently; only restart polling when membership changes.
  const deviceKey = [...new Set(devices.map((device) => Number(device.id)))].sort((a, b) => a - b).join(',');

  useEffect(() => {
    let cancelled = false;
    let timer;
    const deviceIds = deviceKey ? deviceKey.split(',').map(Number) : [];
    const verifiedAdb = new Map();
    setConnections({ remotePairing: {}, adb: {}, nativeNodes: {} });
    setLgTargets({});
    if (!siteId || !deviceIds.length) return undefined;

    const refresh = async () => {
      try {
        const response = await axios.get(`/api/site-control/sites/${siteId}`, { ...authConfig(), timeout: 10000 });
        if (cancelled) return;
        const targets = response.data?.targets;
        if (!Array.isArray(targets)) throw new Error('target_status_unavailable');
        setLgTargets(Object.fromEntries(targets.filter((target) => target.protocol === 'lg_webos_remote').map((target) => [Number(target.deviceId), target])));
        // Already returned by the site snapshot; no additional discovery or device read.
        const nativeNodes = Object.fromEntries((response.data?.bridgeNodes || []).map((node) => [Number(node.id), node]));
        const byDevice = new Map();
        // A physical device can use a separate, linked control target in another area.
        targets.forEach((target) => {
          if (target.linkedDeviceId && (target.adb || !byDevice.has(Number(target.linkedDeviceId)))) {
            byDevice.set(Number(target.linkedDeviceId), target);
          }
        });
        targets.forEach((target) => {
          if (target.adb || !byDevice.get(Number(target.deviceId))?.adb) byDevice.set(Number(target.deviceId), target);
        });
        const remotePairing = {};
        const adb = {};
        const checks = new Map();
        deviceIds.forEach((deviceId) => {
          const target = byDevice.get(deviceId);
          remotePairing[deviceId] = Boolean(target?.googleRemote?.paired
            ?? (target?.protocol === 'android_tv_remote_v2' && target?.paired));
          const authorization = target?.adbAuthorization;
          const authMode = target?.adb?.authMode;
          const authorized = target?.adb && (authorization?.paired === true || authorization?.trustState === 'not_required')
            && (!authorization.authMode || authorization.authMode === authMode);
          adb[deviceId] = { authMode, status: authorized ? 'checking'
            : authorization?.trustState === 'identity_unavailable' ? 'unavailable' : 'unpaired' };
          if (authorized && authMode === 'tls' && target.adbConnection?.managed === true) {
            const health = target.adbConnection;
            adb[deviceId] = { authMode, status: health.state, checkWirelessDebugging: health.checkWirelessDebugging === true,
              recovering: health.recovering, checkedAtEpochMs: health.checkedAtEpochMs,
              lastConnectedAtEpochMs: health.lastConnectedAtEpochMs, nextCheckAtEpochMs: health.nextCheckAtEpochMs, reason: health.reason };
          } else if (authorized) {
            // Keep the completed result visible during a routine probe, but
            // never carry it across an endpoint or authorization change.
            const scope = JSON.stringify([target.deviceId, target.adb, authorization.hostIdentityId,
              authorization.generation, authorization.pairedAt, authorization.publicKeyFingerprint]);
            const previous = verifiedAdb.get(deviceId);
            if (previous?.scope === scope) Object.assign(adb[deviceId], { status: previous.status, checkedAtEpochMs: previous.checkedAtEpochMs });
            checks.set(target.deviceId, target);
          }
          if (authMode === 'tls' && Object.prototype.hasOwnProperty.call(target || {}, 'wirelessDebuggingRecovery')) {
            adb[deviceId].wirelessDebuggingRecovery = target.wirelessDebuggingRecovery;
          }
          if (!authorized) verifiedAdb.delete(deviceId);
        });
        setConnections({ remotePairing, adb, nativeNodes });
        await Promise.all([...checks.values()].map(async (target) => {
          let connected = false;
          try {
            // Explicit ADB selection prevents a connectivity check from sending a navigation key.
            const result = await axios.post(`/api/site-control/targets/${target.deviceId}/action`, {
              controlMethod: 'adb_remote', action: 'ADB_TEST_CONNECTION',
            }, { ...authConfig(), timeout: 65000 });
            connected = result.data?.success === true && result.data?.status === 'completed';
          } catch (_) { /* A paired target whose connection cannot be verified is disconnected. */ }
          if (cancelled) return;
          const scope = JSON.stringify([target.deviceId, target.adb, target.adbAuthorization.hostIdentityId,
            target.adbAuthorization.generation, target.adbAuthorization.pairedAt, target.adbAuthorization.publicKeyFingerprint]);
          deviceIds.forEach((deviceId) => {
            if (byDevice.get(deviceId)?.deviceId === target.deviceId) {
              verifiedAdb.set(deviceId, { scope, status: connected ? 'connected' : 'disconnected', checkedAtEpochMs: Date.now() });
            }
          });
          setConnections((current) => ({ ...current, adb: Object.fromEntries(Object.entries(current.adb).map(([id, state]) => [id,
            byDevice.get(Number(id))?.deviceId === target.deviceId
              ? { ...state, status: connected ? 'connected' : 'disconnected', checkedAtEpochMs: Date.now() } : state,
          ])) }));
        }));
      } catch (_) {
        if (!cancelled) setLgTargets({});
        if (!cancelled) setConnections({ remotePairing: {}, nativeNodes: {}, adb: Object.fromEntries(deviceIds.map((id) => [id, { status: 'unavailable' }])) });
      } finally {
        // Schedule after completion so slow connection checks cannot overlap.
        if (!cancelled) timer = window.setTimeout(refresh, 30000);
      }
    };
    refresh();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [siteId, deviceKey]);

  return { ...connections, lgTargets };
}
